// src/context/MasterDataContext.jsx
// ─────────────────────────────────────────────────────────────────
// Loads all master data once on login and caches it in localStorage.
// Now includes COHORT_SCHEDULES — per-cohort call stage definitions.
//
// New hooks:
//   useAllSchedules()               → all cohort schedules []
//   useScheduleForCohort(cohortName) → resolved CallStage[] for one employee
//   useCallSchedule()               → legacy: first/default schedule stages
// ─────────────────────────────────────────────────────────────────

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import api from '../services/api';

const STORAGE_KEY     = 'ejt_master_data';
const STORAGE_VERSION = 'v2';   // bump so old v1 cache is discarded

const EMPTY = {
  CALL_SCHEDULE:    [],   // legacy flat list (fallback)
  DAY10_QUESTIONS:  [],
  MANAGER_FIELDS:   [],
  STATUS_CONFIG:    {},
  CSV_COLUMN_MAP:   {},
  COHORT_SCHEDULES: [],   // [{scheduleKey, label, stages:[{key,day,label,hasQuestions}]}]
};

// ── Fallback schedules used if API is unavailable ────────────────
const FALLBACK_SCHEDULES = [
  {
    scheduleKey: 'pratham',
    label:       'Pratham',
    stages: [
      { key:'day10',  day:10,  label:'Day 10',  hasQuestions:true  },
      { key:'day30',  day:30,  label:'Day 30',  hasQuestions:false },
      { key:'day60',  day:60,  label:'Day 60',  hasQuestions:false },
      { key:'day105', day:105, label:'Day 105', hasQuestions:false },
      { key:'day150', day:150, label:'Day 150', hasQuestions:false },
      { key:'day180', day:180, label:'Day 180', hasQuestions:false },
    ],
  },
  {
    scheduleKey: 'campus',
    label:       'Campus',
    stages: [
      { key:'day30',  day:30,  label:'Day 30',  hasQuestions:true  },
      { key:'day60',  day:60,  label:'Day 60',  hasQuestions:false },
      { key:'day90',  day:90,  label:'Day 90',  hasQuestions:false },
      { key:'day120', day:120, label:'Day 120', hasQuestions:false },
      { key:'day150', day:150, label:'Day 150', hasQuestions:false },
      { key:'day180', day:180, label:'Day 180', hasQuestions:false },
    ],
  },
];

// ── Resolve schedule for a given cohortName string ───────────────
function resolveSchedule(cohortName, allSchedules) {
  const schedules = allSchedules?.length ? allSchedules : FALLBACK_SCHEDULES;
  if (!cohortName) return schedules[0];

  const lower = cohortName.trim().toLowerCase();
  // Exact key match
  const exact = schedules.find(s => s.scheduleKey === lower);
  if (exact) return exact;
  // Prefix match: "Pratham 03 Dec 2025" → key "pratham"
  const prefix = schedules.find(s => lower.startsWith(s.scheduleKey));
  if (prefix) return prefix;
  // Fallback: first schedule
  return schedules[0];
}

// ── Cache helpers ────────────────────────────────────────────────
function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed._version === STORAGE_VERSION) return parsed.data;
  } catch (_) {}
  return null;
}

const Ctx = createContext({
  masterData:        EMPTY,
  isLoaded:          false,
  loadMasterData:    async () => {},
  refreshMasterData: async () => {},
});

export function MasterDataProvider({ children }) {
  const cached = readCache();
  const [masterData, setMasterData] = useState(cached ?? EMPTY);
  const [isLoaded,   setIsLoaded]   = useState(!!cached);

  const isLoadedRef = useRef(!!cached);
  const fetchingRef = useRef(false);

  const fetchAndStore = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      // Fetch general master data
      const { data } = await api.get('/master-data');

      // Fetch cohort schedules from dedicated endpoint
      let cohortSchedules = FALLBACK_SCHEDULES;
      try {
        const { data: schedData } = await api.get('/cohort-schedule');
        if (Array.isArray(schedData) && schedData.length)
          cohortSchedules = schedData;
      } catch (_) {
        console.warn('[MasterData] cohort-schedule API unavailable — using fallback');
      }

      const merged = { ...data, COHORT_SCHEDULES: cohortSchedules };

      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        _version:   STORAGE_VERSION,
        _fetchedAt: new Date().toISOString(),
        data:       merged,
      }));

      setMasterData(merged);
      setIsLoaded(true);
      isLoadedRef.current = true;
    } catch (err) {
      console.error('[MasterData] ❌ Fetch failed:', err.message);
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  const loadMasterData = useCallback(async () => {
    if (isLoadedRef.current) return;
    await fetchAndStore();
  }, [fetchAndStore]);

  const refreshMasterData = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    isLoadedRef.current = false;
    setIsLoaded(false);
    await fetchAndStore();
  }, [fetchAndStore]);

  return (
    <Ctx.Provider value={{ masterData, isLoaded, loadMasterData, refreshMasterData }}>
      {children}
    </Ctx.Provider>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────

export function useMasterData()    { return useContext(Ctx); }

/**
 * Returns the shared/fallback question set (backward compat).
 * Use useQuestionsForCohort() for per-cohort questions.
 */
export const useDay10Questions = () => useContext(Ctx).masterData.DAY10_QUESTIONS || [];
export const useManagerFields  = () => useContext(Ctx).masterData.MANAGER_FIELDS;
export const useStatusConfig   = () => useContext(Ctx).masterData.STATUS_CONFIG;
export const useCsvColumnMap   = () => useContext(Ctx).masterData.CSV_COLUMN_MAP;

/**
 * Returns questions for a specific cohort type.
 * Usage: const questions = useQuestionsForCohort('pratham');
 * Falls back to shared questions if no per-cohort set exists.
 */
export function useQuestionsForCohort(scheduleKey) {
  const byCoort = useContext(Ctx).masterData.QUESTIONS_BY_COHORT || {};
  const shared  = useContext(Ctx).masterData.DAY10_QUESTIONS || [];
  if (!scheduleKey) return shared;
  const key = scheduleKey.trim().toLowerCase();
  return byCoort[key] || shared;
}

/**
 * Returns questions for an employee by their cohortName (resolves scheduleKey automatically).
 * Usage: const questions = useQuestionsForCohortName(employee.cohortName);
 */
export function useQuestionsForCohortName(cohortName) {
  const schedules = useAllSchedules();
  const byCoort   = useContext(Ctx).masterData.QUESTIONS_BY_COHORT || {};
  const shared    = useContext(Ctx).masterData.DAY10_QUESTIONS || [];
  if (!cohortName) return shared;
  const name  = cohortName.trim().toLowerCase();
  const found = schedules.find(s => name === s.scheduleKey || name.startsWith(s.scheduleKey));
  const key   = found?.scheduleKey;
  return (key && byCoort[key]) ? byCoort[key] : shared;
}

/** All cohort schedule definitions */
export function useAllSchedules() {
  const schedules = useContext(Ctx).masterData.COHORT_SCHEDULES;
  return schedules?.length ? schedules : FALLBACK_SCHEDULES;
}

/**
 * Resolve CallStage[] for a specific employee's cohortName.
 * Usage: const stages = useScheduleForCohort(employee.cohortName);
 * Returns e.g. [{key:'day10', day:10, label:'Day 10', hasQuestions:true}, ...]
 */
export function useScheduleForCohort(cohortName) {
  const allSchedules = useContext(Ctx).masterData.COHORT_SCHEDULES;
  return useMemo(
    () => resolveSchedule(cohortName, allSchedules)?.stages ?? FALLBACK_SCHEDULES[0].stages,
    [cohortName, allSchedules],
  );
}

/**
 * Legacy: returns stages for the FIRST schedule (usually Pratham).
 * Prefer useScheduleForCohort(cohortName) in new code.
 */
export const useCallSchedule = () => {
  const allSchedules = useContext(Ctx).masterData.COHORT_SCHEDULES;
  const schedules    = allSchedules?.length ? allSchedules : FALLBACK_SCHEDULES;
  return schedules[0]?.stages ?? [];
};

/**
 * Returns the full schedule object (not just stages) for a cohortName.
 * Includes scheduleKey, label, stages.
 */
export function useScheduleObjectForCohort(cohortName) {
  const allSchedules = useContext(Ctx).masterData.COHORT_SCHEDULES;
  return useMemo(
    () => resolveSchedule(cohortName, allSchedules),
    [cohortName, allSchedules],
  );
}

/**
 * Helper: build a CALL_DAYS map from any stages array.
 * Usage: const CALL_DAYS = buildCallDaysMap(stages);
 * Returns: { day10: 10, day30: 30, ... }
 */
export function buildCallDaysMap(stages) {
  return Object.fromEntries((stages ?? []).map(s => [s.key, s.day]));
}

/**
 * Helper: get status of a call for an employee against their schedule.
 * Returns 'Completed' | 'No Response' | 'Pending' | 'Upcoming'
 */
export function getCallStatusForEmployee(emp, callKey, stages) {
  const callRow = emp.calls?.find(c => c.callKey === callKey);
  if (callRow) return callRow.status;
  if (!emp.dateOfJoining) return 'Upcoming';
  const stage = (stages ?? []).find(s => s.key === callKey);
  if (!stage) return 'Upcoming';
  const daysUntil = new Date(emp.dateOfJoining);
  daysUntil.setDate(daysUntil.getDate() + stage.day);
  return daysUntil > new Date() ? 'Upcoming' : 'Pending';
}